/**
 * preorder.js - Perfect Locks Preorder System
 * ============================================
 *
 * OVERVIEW
 * --------
 * Manages preorder state on the product page. Reads a JSON data map
 * embedded by product-form.liquid, then updates the badge, button label,
 * lead time message, and hidden line item property on every variant change.
 *
 * FILES INVOLVED
 * --------------
 * snippets/product-form.liquid  - Core logic, data map, badge/button markup
 * assets/preorder.js            - This file. Loaded once via layout/theme.liquid
 *                                   on product pages only (template.name == product).
 * assets/merged.css             - Button and message styles (appended at bottom).
 * snippets/klaviyo-back-in-stock.liquid - Fixed to not override ATC visibility.
 * sections/mini-cart.liquid     - Preorder notice on mini cart line items.
 * sections/main-cart.liquid     - Preorder notice on full cart line items.
 * config/settings_schema.json   - Adds Preorder section to theme customizer.
 *
 * HOW TO ACTIVATE
 * ---------------
 * Scenario 1 - Permanent preorder product (slow movers e.g. Machine Wefts):
 *   1. Go to Shopify Admin > Products > [product]
 *   2. Set inventory to 0 on all variants
 *   3. Scroll to Metafields > set preorder.enabled = true
 *   4. Optionally set preorder.lead_time (defaults to theme setting)
 *
 * Scenario 2 - Black Friday mode (any OOS variant store-wide shows preorder):
 *   1. Go to Online Store > Themes > Customize
 *   2. Find the Preorder section in theme settings
 *   3. Toggle "Black Friday mode" ON before the sale, OFF after.
 *
 * METAFIELDS (set on Products in Shopify Admin > Settings > Custom data)
 * -----------------------------------------------------------------------
 *   preorder.enabled      True/False  - turns preorder on for the product
 *   preorder.lead_time    Text        - e.g. "3-4 weeks" (overrides theme default)
 *   preorder.message      Text        - optional custom message below button
 *   preorder.badge_label  Text        - optional button label (default: "Preorder")
 *   IMPORTANT: namespace must be exactly "preorder" (lowercase), keys are case-sensitive.
 *
 * THEME SETTINGS (Customize > Preorder section)
 * ----------------------------------------------
 *   preorder_ignore_stock       - Black Friday mode toggle
 *   preorder_default_lead_time  - Store-wide default lead time
 *   preorder_default_badge_label - Store-wide default button label
 *
 * SHOPIFY FLOWS (set up once, run permanently)
 * --------------------------------------------
 *   Flow 1: Trigger "Product variant out of stock"
 *           Action: Update product variant > Inventory policy = continue
 *   Flow 2: Trigger "Product variant back in stock"
 *           Action: Update product variant > Inventory policy = deny
 *
 * KLAVIYO
 * -------
 * Lead time flows to orders as line item property "_Preorder Lead Time".
 * In Klaviyo templates use: {{ item.properties._Preorder_Lead_Time }}
 * (Klaviyo replaces spaces with underscores in property key names)
 *
 * CHECKOUT WIZ
 * ------------
 * Add a conditional banner block in Checkout Wiz:
 *   Condition: line item property "_Preorder Lead Time" is present
 *   Message: "Your order contains a preorder item. Ships within lead time shown in cart."
 *
 * ACTIVATION LOGIC
 * ----------------
 * Preorder button shows when ALL of these are true:
 *   - Variant inventory_quantity <= 0 (or inventory not tracked)
 *   - AND: preorder.enabled = true on the product OR Black Friday mode is ON
 */

(function () {
  'use strict';

  // ─── Constants ────────────────────────────────────────────────────────────

  const BUTTON_LABEL_PREORDER = 'Preorder - Ships in {lead_time}';
  const BUTTON_LABEL_DEFAULT  = 'Add to Cart';
  const PROPERTY_KEY          = '_Preorder Lead Time'; // underscore = hidden from customer UI
  const CART_ATTRIBUTE_KEY    = 'Contains Preorder Items';

  // ─── Helpers ──────────────────────────────────────────────────────────────

  /**
   * Read the preorder data map injected by product-form.liquid.
   * Returns an object keyed by variant ID, or empty object if not found.
   *
   * @param {HTMLElement} formEl - the product form element
   * @returns {Object}
   */
  function getPreorderMap(formEl) {
    const scriptEl = formEl
      ? formEl.querySelector('[data-preorder-map]')
      : document.querySelector('[data-preorder-map]');
    if (!scriptEl) return {};
    try {
      return JSON.parse(scriptEl.textContent);
    } catch (e) {
      console.warn('[preorder.js] Could not parse preorder data map:', e);
      return {};
    }
  }

  /**
   * Find all product form root elements on the page.
   * Handles standard PDP, featured-product section, and quick-buy drawer.
   *
   * @returns {NodeList}
   */
  function getProductForms() {
    return document.querySelectorAll('[data-preorder-form]');
  }

  // ─── DOM updaters ─────────────────────────────────────────────────────────

  /**
   * Update the preorder badge element's visibility and text.
   *
   * @param {HTMLElement} container - scoped product form container
   * @param {Object|null} preorderData - preorder data for the selected variant, or null
   */
  function updateBadge(container, preorderData) {
    const badge = container.querySelector('[data-preorder-badge]');
    if (!badge) return;

    if (preorderData && preorderData.enabled === true) {
      badge.textContent = preorderData.badge_label || 'Preorder';
      badge.setAttribute('aria-label', preorderData.badge_label || 'Preorder');
      badge.style.display = '';
    } else {
      badge.style.display = 'none';
    }
  }

  /**
   * Update the preorder message block's visibility and content.
   *
   * @param {HTMLElement} container
   * @param {Object|null} preorderData
   */
  function updateMessage(container, preorderData) {
    const message  = container.querySelector('[data-preorder-message]');
    const leadSpan = container.querySelector('[data-preorder-lead-time]');

    if (!message) return;

    if (preorderData && preorderData.enabled === true) {
      if (leadSpan) {
        leadSpan.textContent = preorderData.lead_time || '3-4 weeks';
      }

      if (preorderData.message) {
        const textEl = message.querySelector('[data-preorder-message-text]');
        if (textEl) textEl.textContent = preorderData.message;
      }

      message.setAttribute(
        'aria-label',
        'Preorder shipping information: ships in ' + (preorderData.lead_time || '3-4 weeks')
      );
      message.style.display = '';
    } else {
      message.style.display = 'none';
    }
  }

  /**
   * Update the Add to Cart button label and the atc-flex wrapper visibility.
   * Fix #1: preorder variants force the wrapper visible even when Shopify
   * considers the variant unavailable (inventory_policy = deny + qty 0).
   * Fix #2: only treat as preorder active when enabled AND qty <= 0
   * (enforced in Liquid data map - JS reads the already-computed value).
   *
   * @param {HTMLElement} container
   * @param {Object|null} preorderData
   */
  function updateButton(container, preorderData) {
    const btn = container.querySelector('[data-product-add-to-cart-button]');
    if (!btn) return;

    // Don't overwrite if the product-level pre-order suffix is active
    if (container.dataset.templateSuffix === 'pre-order') return;

    const atcWrapper = container.querySelector('[data-atc-wrapper]');
    let label;

    if (preorderData && preorderData.enabled === true) {
      label = BUTTON_LABEL_PREORDER.replace(
        '{lead_time}',
        preorderData.lead_time || '3-4 weeks'
      );

      // Fix #1: force ATC wrapper visible for preorder variants regardless of
      // Shopify's available state. The variant MUST have inventory_policy = continue
      // for the form submission to succeed - warn if it may not.
      if (atcWrapper) {
        atcWrapper.style.display = '';
      }

      // Fix #1: warn if the variant may be blocking submission due to policy
      if (preorderData.needs_continue_policy) {
        console.warn(
          '[preorder.js] Variant has preorder.enabled = true. ' +
          'Ensure its inventory policy is set to "Continue selling when out of stock" ' +
          'in Shopify Admin, otherwise cart submission will fail when stock hits zero.'
        );
      }
    } else {
      // Restore original label
      label = btn.dataset.originalLabel || BUTTON_LABEL_DEFAULT;

      // Hide ATC wrapper if variant is genuinely unavailable
      // Re-check availability from the select element
      const masterSelect = container.querySelector('select[name="id"], select.master-select');
      if (atcWrapper && masterSelect) {
        const selectedOption = masterSelect.options[masterSelect.selectedIndex];
        if (selectedOption && selectedOption.disabled) {
          atcWrapper.style.display = 'none';
        } else {
          atcWrapper.style.display = '';
        }
      }
    }

    // Store original on first run.
    // If loader-button has already run connectedCallback, the button's visible
    // text lives inside btn.firstElementChild (loader-button__text), not directly
    // in btn.textContent. Read from there so we capture the real label, not the
    // full inner HTML including the hidden spinner span.
    if (!btn.dataset.originalLabel) {
      const textEl = btn.querySelector('.loader-button__text');
      btn.dataset.originalLabel = textEl
        ? textEl.textContent.trim()
        : btn.textContent.trim();
    }

    // Write label into loader-button__text if present, otherwise fall back to
    // btn.textContent. Writing to btn.textContent would destroy the spinner span
    // that loader-button injected, permanently breaking the loading animation.
    const loaderTextEl = btn.querySelector('.loader-button__text');
    if (loaderTextEl) {
      loaderTextEl.textContent = label;
    } else {
      btn.textContent = label;
    }
    btn.setAttribute('aria-label', label);
    btn.dataset.buttonContent = label;
  }

  /**
   * Update the hidden line item property input that carries preorder info
   * through to the order (and therefore to Klaviyo).
   *
   * @param {HTMLElement} container
   * @param {Object|null} preorderData
   */
  function updateLineItemProperty(container, preorderData) {
    const input = container.querySelector('[data-preorder-property]');
    if (!input) return;

    if (preorderData && preorderData.enabled === true) {
      input.value    = preorderData.lead_time || '3-4 weeks';
      input.disabled = false;
    } else {
      input.value    = '';
      input.disabled = true;
    }
  }

  /**
   * Update the cart attribute to flag whether the cart contains preorder items.
   * This is read at checkout to show the preorder notice.
   * Uses Shopify Cart API.
   *
   * @param {boolean} hasPreorder
   */
  function updateCartAttribute(hasPreorder) {
    const attributes = {};
    attributes[CART_ATTRIBUTE_KEY] = hasPreorder ? 'true' : '';

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch(function (err) {
      console.warn('[preorder.js] Cart attribute update failed:', err);
    });
  }

  // ─── Core state update ────────────────────────────────────────────────────

  /**
   * Apply all DOM + cart updates for a given variant ID.
   *
   * @param {HTMLElement} container - scoped product form container
   * @param {string|number} variantId
   * @param {Object} preorderMap - full map of variantId -> preorderData
   */
  function applyPreorderState(container, variantId, preorderMap) {
    const raw = preorderMap[String(variantId)] || null;

    // Normalise: only treat as active preorder if enabled is EXPLICITLY true.
    // Guards against loose truthiness on the object itself when enabled=false.
    const preorderData = (raw && raw.enabled === true) ? raw : null;

    updateBadge(container, preorderData);
    updateMessage(container, preorderData);
    updateButton(container, preorderData);
    updateLineItemProperty(container, preorderData);

    // Update cart attribute only if variant is actively being selected
    // (not on initial page load scan - handled separately in initCartAttributeSync)
    if (container.dataset.preorderInit === 'true') {
      updateCartAttribute(preorderData !== null);
    }
  }

  // ─── Cart attribute sync on page load ────────────────────────────────────

  /**
   * On cart/checkout pages, read existing cart items and sync the cart attribute
   * based on whether any line item carries the preorder property.
   */
  function initCartAttributeSync() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const hasPreorder = cart.items.some(function (item) {
          return item.properties && item.properties[PROPERTY_KEY];
        });
        updateCartAttribute(hasPreorder);
      })
      .catch(function (err) {
        console.warn('[preorder.js] Cart fetch failed:', err);
      });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    const forms = getProductForms();
    if (!forms.length) return;

    forms.forEach(function (container) {
      const preorderMap = getPreorderMap(container);
      if (!Object.keys(preorderMap).length) return;

      // Set initial state for the currently selected variant
      const selectedInput = container.querySelector('input[name="id"]:not([disabled])');
      const initialId     = selectedInput ? selectedInput.value : null;

      if (initialId) {
        applyPreorderState(container, initialId, preorderMap);
      }

      // Mark as initialised so subsequent changes trigger cart attribute updates
      container.dataset.preorderInit = 'true';

      // Listen for variant changes dispatched by the theme
      // The Focal theme dispatches 'variant:changed' on the form with detail.variant
      container.addEventListener('variant:changed', function (e) {
        const variant = e.detail && e.detail.variant;
        if (!variant) return;
        applyPreorderState(container, variant.id, preorderMap);
      });

      // Fallback: also watch the hidden ID input directly for themes that
      // update it without dispatching a custom event
      const idInput = container.querySelector('input[name="id"]');
      if (idInput) {
        const observer = new MutationObserver(function (mutations) {
          mutations.forEach(function (m) {
            if (m.attributeName === 'value' || m.attributeName === 'disabled') {
              const currentId = idInput.value;
              if (currentId) {
                applyPreorderState(container, currentId, preorderMap);
              }
            }
          });
        });
        observer.observe(idInput, { attributes: true });
      }
    });

    // Sync cart attribute on load
    initCartAttributeSync();
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();